import { memo, useEffect, useRef, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  AlertCircle, Check, ChevronLeft, ChevronRight, CircleDollarSign, Copy, Download, Film,
  FolderPlus, Image as ImageIcon, ImagePlus, LoaderCircle, Maximize2, Music2, Play, RefreshCw, Sparkles, Trash2, Upload, Video,
} from 'lucide-react';
import ReferencePrompt from './ReferencePrompt.jsx';
import { canvasImagePreviewUrl, generatorPreviewSize, mediaNodeSize } from './canvas-utils.js';
import VideoFramePreview from './VideoFramePreview.jsx';

function NodeHandles() {
  return <>
    <Handle className="canvas-handle canvas-handle--target" type="target" position={Position.Left} />
    <Handle className="canvas-handle canvas-handle--source" type="source" position={Position.Right} />
  </>;
}

function QuickNodeActions({ id, data, framePreview = null }) {
  const [frameOpen, setFrameOpen] = useState(false);
  const frameActionRef = useRef(null);
  useEffect(() => {
    if (!frameOpen) return undefined;
    const closeOutside = (event) => {
      if (!frameActionRef.current?.contains(event.target)) setFrameOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside, true);
    return () => document.removeEventListener('pointerdown', closeOutside, true);
  }, [frameOpen]);
  const actions = data?._actions;
  if (!actions) return null;
  const canDerive = Boolean(data._canDerive);
  const resultIndex = Math.min(Math.max(0, Number(data.activeResult || 0)), Math.max(0, (data.results || []).length - 1));
  const activeResult = data.results?.[resultIndex];
  const media = data.kind
    ? { kind: data.kind, url: data.url, downloadUrl: data.downloadUrl, name: data.name }
    : data.videoUrl
      ? { kind: 'video', url: data.videoUrl, downloadUrl: data.videoUrl, name: data.title || '生成视频' }
      : activeResult?.url
        ? { kind: 'image', url: activeResult.url, downloadUrl: activeResult.downloadUrl || activeResult.url, name: `${data.title || '生成图片'}-${resultIndex + 1}` }
        : null;
  return <div className="node-quick-actions nodrag nopan" onPointerDown={(event) => event.stopPropagation()}>
    <button type="button" onClick={() => actions.duplicate(id)} title="复制节点"><Copy size={15} /></button>
    <button type="button" disabled={!canDerive} onClick={() => actions.derive(id, 'image')} title="向右派生生图"><ImagePlus size={15} /></button>
    <button type="button" disabled={!canDerive} onClick={() => actions.derive(id, 'video')} title="向右派生生视频"><Video size={15} /></button>
    {framePreview && <span ref={frameActionRef} className={`node-frame-action ${frameOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="is-frame-preview"
        title="抽帧"
        onClick={(event) => {
          event.stopPropagation();
          const nextOpen = !frameOpen;
          setFrameOpen(nextOpen);
          if (!nextOpen) return;
          // The capture state lives inside VideoFramePreview. Keep one source
          // of truth and route the toolbar click to its capture command.
          event.currentTarget.closest('.node-quick-actions')
            ?.querySelector('.video-frame-tool__bar > button:not(.is-clear)')
            ?.click();
        }}
      ><Film size={15} /></button>
      <span className="node-frame-popover" onPointerDown={(event) => event.stopPropagation()}>{framePreview}</span>
    </span>}
    <button type="button" className="is-download" disabled={!media} onClick={() => actions.download(media)} title="下载"><Download size={15} /></button>
    <button type="button" className="is-danger" onClick={() => actions.removeNode(id)} title="删除节点"><Trash2 size={15} /></button>
  </div>;
}

function StatusBadge({ status, progress = 0 }) {
  if (['queued', 'processing', 'running', 'pending'].includes(status)) {
    return <span className="node-status is-running"><LoaderCircle className="spin" size={12} />{Math.max(2, Number(progress || 0))}%</span>;
  }
  if (status === 'completed') return <span className="node-status is-done"><Check size={12} />已完成</span>;
  if (status === 'failed' || status === 'review') return <span className="node-status is-error"><AlertCircle size={12} />需重试</span>;
  return <span className="node-status">待生成</span>;
}

function MediaActions({ onDownload, onPreview }) {
  return <div className="node-media-actions nodrag">
    <button type="button" onClick={onDownload} title="下载"><Download size={18} /></button>
    <button type="button" onClick={onPreview} title="放大预览"><Maximize2 size={18} /></button>
  </div>;
}

function SaveMaterialButton({ onClick }) {
  return <button type="button" className="node-save-material nodrag" onClick={onClick} title="加入收藏夹"><FolderPlus size={14} />收藏</button>;
}

function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setDuration(0);
  }, [url]);

  const toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play(); else audio.pause();
  };

  return <div className="audio-player audio-node-drag-surface" data-drag-surface="audio">
    <button type="button" className="audio-player__toggle nodrag" onPointerDown={(event) => event.stopPropagation()} onClick={toggle} title={playing ? '暂停音频' : '播放音频'}>
      {playing ? <span className="audio-player__pause" /> : <Play size={20} fill="currentColor" />}
    </button>
    <div className="audio-player__track" aria-hidden="true"><i style={{ width: `${progress * 100}%` }} /></div>
    <time>{Math.floor(progress * duration / 60)}:{String(Math.floor(progress * duration % 60)).padStart(2, '0')} / {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</time>
    <audio
      ref={audioRef}
      src={url}
      preload="metadata"
      onLoadedMetadata={(event) => setDuration(Number(event.currentTarget.duration || 0))}
      onTimeUpdate={(event) => setProgress(Number(event.currentTarget.currentTime || 0) / Math.max(0.01, Number(event.currentTarget.duration || 0)))}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => { setPlaying(false); setProgress(0); }}
    />
  </div>;
}

function HoverVideo({ className, src, poster, controls = true }) {
  const videoRef = useRef(null);
  const play = () => {
    const video = videoRef.current;
    if (video) video.muted = true;
    if (video?.paused) void video.play().catch(() => {});
  };
  const pause = () => {
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
  };
  return <video
    ref={videoRef}
    className={className}
    src={src}
    poster={poster || undefined}
    controls={controls}
    preload="metadata"
    playsInline
    onMouseEnter={play}
    onMouseLeave={pause}
  />;
}

export const MediaNode = memo(function MediaNode({ id, data, selected }) {
  const audioPressRef = useRef(null);
  const size = mediaNodeSize(data.width, data.height, data.kind);
  const Icon = data.kind === 'image' ? ImageIcon : data.kind === 'video' ? Film : Music2;
  const displayUrl = data.kind === 'image' ? canvasImagePreviewUrl(data.url, data.previewUrl) : data.url;
  const hasMedia = Boolean(displayUrl);
  return (
    <article className={`canvas-node media-node kind-${data.kind} ${selected ? 'is-selected' : ''} ${data._highlighted ? 'is-reference-highlight' : ''} ${data._edgeHighlighted ? 'is-edge-highlighted' : ''}`} style={{ width: size.width, '--media-height': `${Math.max(88, size.height - 38)}px` }}>
      <NodeHandles />
      <QuickNodeActions id={id} data={data} framePreview={data.kind === 'video' && hasMedia ? <VideoFramePreview
        url={data.url}
        onPreview={data._actions.preview}
        onSelectFrame={(frame) => data._actions.addVideoFrame(frame, id)}
        onNotify={data._actions.notify}
      /> : null} />
      <header className={`canvas-node__header ${data.kind === 'audio' ? 'audio-node-drag-surface' : ''}`}>
        <Icon size={14} />
        <strong>{data._displayLabel || data.name || '素材'}</strong>
        {data.uploading && <LoaderCircle className="spin" size={13} />}
      </header>
      <div className={`media-node__body ${data.kind === 'audio' ? 'audio-node-drag-surface' : ''}`} style={data.kind === 'audio' ? undefined : { height: size.height - 38 }}>
        {!hasMedia ? data.kind === 'audio' ? (
          <div
            className="empty-media audio-node-drag-surface"
            role="button"
            tabIndex={0}
            aria-disabled={data.uploading ? 'true' : 'false'}
            onPointerDown={(event) => { audioPressRef.current = { x: event.clientX, y: event.clientY }; }}
            onClick={(event) => {
              const start = audioPressRef.current;
              audioPressRef.current = null;
              const moved = start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5;
              if (!moved && !data.uploading) data._actions.pickForNode(id, data.kind);
            }}
            onKeyDown={(event) => {
              if (!data.uploading && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                data._actions.pickForNode(id, data.kind);
              }
            }}
          >
            {data.uploading ? <LoaderCircle className="spin" size={20} /> : <Upload size={20} />}<span>{data.uploading ? '正在优化预览' : '选择音频'}</span>
          </div>
        ) : (
          <button className="empty-media nodrag" type="button" disabled={data.uploading} onClick={() => data._actions.pickForNode(id, data.kind)}>
            {data.uploading ? <LoaderCircle className="spin" size={20} /> : <Upload size={20} />}<span>{data.uploading ? '正在优化预览' : `选择${data.kind === 'image' ? '图片' : '视频'}`}</span>
          </button>
        ) : data.kind === 'image' ? (
          <img src={displayUrl} alt={data.name || '图片素材'} draggable="false" loading="lazy" decoding="async" />
        ) : data.kind === 'video' ? (
          <HoverVideo className="generator-video" src={data.url} />
        ) : (
          <AudioPlayer url={data.url} />
        )}
        {hasMedia && data.kind !== 'audio' && <MediaActions
          onDownload={() => data._actions.download({ kind: data.kind, url: data.url, downloadUrl: data.downloadUrl, name: data.name })}
          onPreview={() => data._actions.preview({ kind: data.kind, url: data.url, downloadUrl: data.downloadUrl, poster: data.kind === 'video' ? data.poster : '', name: data.name })}
        />}
        {hasMedia && <SaveMaterialButton onClick={() => data._actions.saveMaterial({
          kind: data.kind, url: data.url, previewUrl: displayUrl, poster: data.poster || '', name: data._displayLabel || data.name,
          taskId: data.sourceTaskId || '', imageIndex: Number(data.sourceIndex || 0),
        })} />}
      </div>
      {data.uploading && <i className="media-upload-progress" />}
    </article>
  );
});

function ResultPreview({ data, id, kind }) {
  if (kind === 'video') {
    if (data.videoUrl) return <>
      <HoverVideo className="generator-video" src={data.videoUrl} poster={data.coverUrl} />
      <MediaActions
        onDownload={() => data._actions.download({ kind: 'video', url: data.videoUrl, downloadUrl: data.videoUrl, name: data.title || '生成视频' })}
        onPreview={() => data._actions.preview({ kind: 'video', url: data.videoUrl, downloadUrl: data.videoUrl, poster: data.coverUrl, name: data.title || '生成视频' })}
      />
      <SaveMaterialButton onClick={() => data._actions.saveMaterial({
        kind: 'video', url: data.videoUrl, previewUrl: data.coverUrl || '', poster: data.coverUrl || '',
        name: data.title || '生成视频',
      })} />
    </>;
    return <div className="generator-empty"><Film size={26} /><span>{['queued', 'processing', 'running'].includes(data.status) ? '视频生成中' : '等待生成视频'}</span></div>;
  }
  const results = data.results || [];
  const index = Math.min(Math.max(0, Number(data.activeResult || 0)), Math.max(0, results.length - 1));
  const active = results[index];
  if (!active?.url) return <div className="generator-empty"><ImageIcon size={26} /><span>{['queued', 'processing', 'running'].includes(data.status) ? '图片生成中' : '等待生成图片'}</span></div>;
  return <>
    <img src={canvasImagePreviewUrl(active.url, active.previewUrl)} alt={`生成结果 ${index + 1}`} draggable="false" loading="lazy" decoding="async" />
    <MediaActions
      onDownload={() => data._actions.download({ kind: 'image', url: active.url, downloadUrl: active.downloadUrl || active.url, name: `${data.title || '生成图片'}-${index + 1}` })}
      onPreview={() => data._actions.preview({ kind: 'image', url: active.url, downloadUrl: active.downloadUrl || active.url, name: `${data.title || '生成图片'} ${index + 1}` })}
    />
    <SaveMaterialButton onClick={() => data._actions.saveMaterial({
      kind: 'image', url: active.url, previewUrl: canvasImagePreviewUrl(active.url, active.previewUrl),
      name: `${data.title || '生成图片'} ${index + 1}`, taskId: data.taskId || '', imageIndex: index,
    })} />
    {results.length > 1 && <div className="result-switcher nodrag">
      <button type="button" onClick={() => data._actions.update(id, { activeResult: (index - 1 + results.length) % results.length })}><ChevronLeft size={14} /></button>
      <span>{index + 1} / {results.length}</span>
      <button type="button" onClick={() => data._actions.update(id, { activeResult: (index + 1) % results.length })}><ChevronRight size={14} /></button>
    </div>}
  </>;
}

function GeneratorHeader({ kind, data }) {
  const Icon = kind === 'image' ? ImageIcon : Film;
  return <header className="canvas-node__header generator-header"><Icon size={16} /><strong>{kind === 'image' ? '图片生成' : '视频生成'}</strong><StatusBadge status={data.status} progress={data.progress} /></header>;
}

function GeneratorFooter({ id, data, kind, cost, incompatible, model }) {
  const running = ['queued', 'processing', 'running', 'pending'].includes(data.status);
  const failed = ['failed', 'review'].includes(data.status);
  const shownCost = Number.isFinite(Number(cost)) ? Number(cost).toFixed(2) : '0.00';
  const imageCount = Math.max(1, Number(data.count) || 1);
  return <footer className="generator-footer nodrag">
    <div className="generator-cost"><CircleDollarSign size={13} /><span>{kind === 'image' ? `总计 ${shownCost} 积分 · ${imageCount} 张` : `${shownCost} 积分/次`}</span></div>
    <button
      type="button"
      className="generator-submit"
      disabled={running || incompatible || !model}
      onClick={() => data._actions.generate(id)}
      title={running ? '正在提交生成任务' : failed ? '重新提交生成任务' : '提交生成任务'}
      aria-label={running ? '正在提交生成任务' : failed ? '重新提交生成任务' : '提交生成任务'}
    >
      {running ? <LoaderCircle className="spin" size={15} /> : failed ? <RefreshCw size={15} /> : kind === 'image' ? <Sparkles size={15} /> : <Play size={15} />}
    </button>
  </footer>;
}

export const ImageGeneratorNode = memo(function ImageGeneratorNode({ id, data, selected }) {
  const config = data._config || {};
  const models = config.models || [];
  const selectedModel = models.find((item) => item.id === data.model) || models[0];
  const ratios = selectedModel?.ratios || config.generation?.ratios || ['1:1'];
  const resolutions = selectedModel?.resolutions || config.generation?.resolutions || ['2K'];
  const preview = generatorPreviewSize(data.ratio || '1:1');
  const width = Math.max(430, preview.width);
  const unitCost = Number(selectedModel?.costByResolution?.[data.resolution] ?? selectedModel?.cost ?? selectedModel?.costPerImage ?? 0);
  const imageCount = Math.max(1, Number(data.count) || 1);
  const cost = Number((unitCost * imageCount).toFixed(2));
  const incompatible = Boolean(data._references?.length && selectedModel?.supportsReferenceImages === false);
  const promptLimit = Number(selectedModel?.promptLimit ?? selectedModel?.maxPromptChars ?? 0) || null;
  const promptLength = Array.from(String(data.prompt || '')).length;
  const promptTooLong = Boolean(promptLimit && promptLength > promptLimit);
  return (
    <article className={`canvas-node generator-node image-generator ${selected ? 'is-selected' : ''} ${data._highlighted ? 'is-reference-highlight' : ''} ${data._edgeHighlighted ? 'is-edge-highlighted' : ''}`} style={{ width, '--preview-height': `${preview.height}px` }}>
      <NodeHandles />
      <QuickNodeActions id={id} data={data} />
      <GeneratorHeader kind="image" data={data} />
      <div className="generator-preview" style={{ width: preview.width, height: preview.height }}><ResultPreview data={data} id={id} kind="image" /></div>
      {selected && <div className="generator-expandable is-open"><div className="generator-panel">
        <ReferencePrompt
          value={data.prompt}
          references={data._mentions}
          options={data._references}
          onChange={(prompt) => data._actions.update(id, { prompt })}
          height={data.promptHeight}
          onHeightChange={(promptHeight) => data._actions.update(id, { promptHeight })}
          onAdd={(sourceId) => data._actions.mentionReference(sourceId, id)}
          onRemove={(sourceId) => data._actions.removeMention(sourceId, id)}
          onMentionHover={data._actions.highlightReference}
          placeholder="描述画面，输入 @ 标记已连接图片"
          maxLength={promptLimit}
        />
        {incompatible && <p className="generator-warning"><AlertCircle size={13} />当前模型不支持参考图，请切换模型或移除引用。</p>}
        {promptTooLong && <p className="generator-warning"><AlertCircle size={13} />当前模型提示词超过 {promptLimit} 字，无法提交。</p>}
        <div className="parameter-row nodrag nowheel">
          <label><span>模型</span><div className="canvas-model-select"><select aria-label="图片模型" value={data.model || selectedModel?.id || ''} onChange={(event) => {
            const next = models.find((item) => item.id === event.target.value);
            data._actions.update(id, {
              model: event.target.value,
              ratio: next?.ratios?.includes(data.ratio) ? data.ratio : (next?.ratios?.[0] || data.ratio),
              resolution: next?.resolutions?.includes(data.resolution) ? data.resolution : (next?.resolutions?.[0] || data.resolution),
            });
          }}>{models.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select>{selectedModel?.highlightLabel && <em className={`zm-model-highlight zm-model-highlight--${selectedModel.highlightTone || 'red'}`}>{selectedModel.highlightLabel}</em>}</div></label>
          <label><span>比例</span><select aria-label="图片比例" value={data.ratio} onChange={(event) => data._actions.update(id, { ratio: event.target.value })}>{ratios.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span>清晰度</span><select aria-label="图片清晰度" value={data.resolution} onChange={(event) => data._actions.update(id, { resolution: event.target.value })}>{resolutions.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="count-field"><span>数量</span><input aria-label="生成数量" type="number" min="1" max={config.generation?.maxImages || 9} value={data.count || 1} onChange={(event) => data._actions.update(id, { count: Math.max(1, Math.min(Number(config.generation?.maxImages || 9), Number(event.target.value) || 1)) })} /></label>
        </div>
        {data.message && data.status !== 'idle' && <p className={`generator-message is-${data.status}`}>{data.message}</p>}
        <GeneratorFooter id={id} data={data} kind="image" cost={cost} model={selectedModel} incompatible={incompatible || promptTooLong} />
      </div></div>}
    </article>
  );
});

export const VideoGeneratorNode = memo(function VideoGeneratorNode({ id, data, selected }) {
  const config = data._config?.video || {};
  const models = config.models || config.modelCatalog || [];
  const selectedModel = models.find((item) => item.id === data.model) || models[0];
  const minDuration = Math.max(1, Number(selectedModel?.minDuration ?? config.minDuration ?? 4));
  const maxDuration = Math.min(60, Math.max(minDuration, Number(selectedModel?.maxDuration ?? config.maxDuration ?? 5)));
  const durations = (selectedModel?.allowedSeconds || Array.from({ length: maxDuration - minDuration + 1 }, (_item, index) => minDuration + index))
    .map(Number).filter((item, index, list) => Number.isInteger(item) && list.indexOf(item) === index).sort((a, b) => a - b);
  const generationMethods = Array.isArray(selectedModel?.generationMethods) && selectedModel.generationMethods.length
    ? selectedModel.generationMethods
    : ['text_to_video'];
  const generationMethodLabels = {
    text_to_video: '文生视频',
    reference_to_video: '参考生视频',
    image_to_video: '图生视频',
    frame_to_video: '首尾帧生视频',
    first_last_frame: '首尾帧生视频',
  };
  const ratios = selectedModel?.ratios || config.ratios || ['16:9', '4:3', '3:4', '1:1', '21:9', '9:16'];
  const resolutions = selectedModel?.resolutions || config.resolutions || ['720P'];
  const preview = generatorPreviewSize(data.ratio || '16:9');
  const width = Math.max(430, preview.width);
  const billingUnit = String(selectedModel?.billingUnit || (Object.keys(selectedModel?.costByVariant || {}).length || Object.keys(selectedModel?.costByDuration || {}).length ? 'fixed_duration' : 'per_task')).toLowerCase();
  const variants = selectedModel?.costByVariant || {};
  const variantCost = variants[`${data.resolution}:${data.duration}`]
    ?? Object.entries(variants).find(([key]) => key.startsWith(`${data.resolution}:${data.duration}:`))?.[1];
  const cost = billingUnit === 'per_second' || billingUnit === 'second'
    ? Number(selectedModel?.costBySecond?.[data.resolution] ?? selectedModel?.billingRate ?? 0) * Number(data.duration || 0)
    : billingUnit === 'fixed_duration' || billingUnit === 'duration'
      ? Number(variantCost ?? ((selectedModel?.resolutions || []).length <= 1 ? selectedModel?.costByDuration?.[String(data.duration)] : undefined) ?? ((selectedModel?.resolutions || []).length <= 1 && !Object.keys(selectedModel?.costByVariant || {}).length ? selectedModel?.costByResolution?.[data.resolution] : undefined) ?? 0)
      : Number(selectedModel?.costPerGeneration) > 0
        ? Number(selectedModel.costPerGeneration)
        : Number(selectedModel?.costByResolution?.[data.resolution] ?? selectedModel?.costPerGeneration ?? config.costPerGeneration ?? 0);
  const textVideoMode = data.generationMode === 'text_to_video';
  const frameVideoMode = ['frame_to_video', 'first_last_frame'].includes(data.generationMode);
  const connectedReferences = data._references || [];
  const frameReferences = connectedReferences.filter((reference) => reference.kind === 'image');
  const frameReferencesValid = frameReferences.length === 2 && connectedReferences.length === 2;
  const promptOptions = textVideoMode ? [] : frameVideoMode ? frameReferences : connectedReferences;
  const promptPlaceholder = textVideoMode
    ? '描述视频内容'
    : frameVideoMode
      ? '描述视频内容，首帧和尾帧按连线顺序确定'
      : '描述视频，输入 @ 引用已连接素材';
  return (
    <article className={`canvas-node generator-node video-generator ${selected ? 'is-selected' : ''} ${data._highlighted ? 'is-reference-highlight' : ''} ${data._edgeHighlighted ? 'is-edge-highlighted' : ''}`} style={{ width, '--preview-height': `${preview.height}px` }}>
      <NodeHandles />
      <QuickNodeActions id={id} data={data} framePreview={data.videoUrl ? <VideoFramePreview
        url={data.videoUrl}
        onPreview={data._actions.preview}
        onSelectFrame={(frame) => data._actions.addVideoFrame(frame, id)}
        onNotify={data._actions.notify}
      /> : null} />
      <GeneratorHeader kind="video" data={data} />
      <div className="generator-preview" style={{ width: preview.width, height: preview.height }}><ResultPreview data={data} id={id} kind="video" /></div>
      {selected && <div className="generator-expandable is-open"><div className="generator-panel">
        {textVideoMode && <p className="canvas-generation-mode-note">文生视频仅使用提示词，不会提交已连接素材。</p>}
        {frameVideoMode && <div className={`canvas-frame-reference-note ${frameReferencesValid ? '' : 'is-warning'}`}>
          <span>首尾帧</span>
          <b>首帧：{frameReferences[0]?.name || '请先连接一张图片'}</b>
          <b>尾帧：{frameReferences[1]?.name || '请再连接一张图片'}</b>
          {!frameReferencesValid && <small>首尾帧模式只接受两张图片，并按连线顺序作为首帧和尾帧。</small>}
        </div>}
        <ReferencePrompt
          value={data.prompt}
          options={promptOptions}
          onChange={(prompt) => data._actions.update(id, { prompt })}
          height={data.promptHeight}
          onHeightChange={(promptHeight) => data._actions.update(id, { promptHeight })}
          onAdd={(sourceId) => data._actions.mentionReference(sourceId, id)}
          onRemove={(sourceId) => data._actions.removeMention(sourceId, id)}
          onMentionHover={data._actions.highlightReference}
          placeholder={promptPlaceholder}
        />
        <div className="parameter-row nodrag nowheel">
          <label><span>模型</span><div className="canvas-model-select"><select aria-label="视频模型" value={data.model || selectedModel?.id || ''} onChange={(event) => {
            const next = models.find((item) => item.id === event.target.value);
            const nextMin = Math.max(1, Number(next?.minDuration ?? config.minDuration ?? 4));
            const nextMax = Math.min(60, Math.max(nextMin, Number(next?.maxDuration ?? config.maxDuration ?? 5)));
            const nextAllowed = (next?.allowedSeconds?.length
              ? next.allowedSeconds
              : Array.from({ length: nextMax - nextMin + 1 }, (_item, index) => nextMin + index)).map(Number);
            data._actions.update(id, {
              model: event.target.value,
              duration: nextAllowed.includes(Number(data.duration)) ? Number(data.duration) : Number(next?.defaultDuration || nextAllowed.at(-1) || nextMax),
              resolution: next?.resolutions?.includes(data.resolution) ? data.resolution : (next?.defaultResolution || next?.resolutions?.[0] || data.resolution),
              ratio: next?.ratios?.includes(data.ratio) ? data.ratio : (next?.ratios?.[0] || data.ratio),
              generationMode: next?.generationMethods?.includes(data.generationMode) ? data.generationMode : (next?.generationMethods?.[0] || ''),
            });
          }}>{models.map((item) => <option value={item.id} key={item.id}>{item.name} · {item.minDuration ?? config.minDuration ?? 4}-{item.maxDuration ?? config.maxDuration ?? 5}秒版</option>)}</select>{selectedModel?.highlightLabel && <em className={`zm-model-highlight zm-model-highlight--${selectedModel.highlightTone || 'red'}`}>{selectedModel.highlightLabel}</em>}</div></label>
          <label><span>比例</span><select aria-label="视频比例" value={data.ratio} onChange={(event) => data._actions.update(id, { ratio: event.target.value })}>{ratios.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span>分辨率</span><select aria-label="视频分辨率" value={resolutions.includes(data.resolution) ? data.resolution : resolutions[0]} onChange={(event) => data._actions.update(id, { resolution: event.target.value })}>{resolutions.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="duration-field"><span>时长</span><select aria-label="视频时长" value={durations.includes(Number(data.duration)) ? Number(data.duration) : durations.at(-1)} onChange={(event) => data._actions.update(id, { duration: Number(event.target.value) })}>{durations.map((duration) => <option key={duration} value={duration}>{duration}秒</option>)}</select></label>
        </div>
        {generationMethods.length > 1 && <div className="parameter-row canvas-generation-methods nodrag nowheel"><label><span>生成方式</span><select aria-label="视频生成方式" value={generationMethods.includes(data.generationMode) ? data.generationMode : generationMethods[0]} onChange={(event) => data._actions.update(id, { generationMode: event.target.value })}>{generationMethods.map((method) => <option key={method} value={method}>{generationMethodLabels[method] || method}</option>)}</select></label></div>}
        {data.message && data.status !== 'idle' && <p className={`generator-message is-${data.status}`}>{data.message}</p>}
        <GeneratorFooter id={id} data={data} kind="video" model={selectedModel} cost={cost} incompatible={false} />
      </div></div>}
    </article>
  );
});
